import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => document.body.classList.remove("no-scroll");
  }, [open]);

  const links = [
    { to: "/", label: "Home", id: NAV.home },
    { to: "/work", label: "Work", id: NAV.work },
    { to: "/services", label: "Services", id: NAV.services },
    { to: "/pricing", label: "Pricing", id: NAV.pricing },
    { to: "/process", label: "Process", id: NAV.process },
    { to: "/contact", label: "Enquire", id: NAV.contact },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${scrolled ? "bg-lak-bg/85 backdrop-blur-md border-b border-black/5" : "bg-transparent"}`}
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 h-20 sm:h-24 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
          <img src="/lak-logo.png" alt="LAK Atelier" className="h-14 sm:h-16 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={`eyebrow link-underline ${pathname === l.to ? "text-lak-text" : "text-lak-soft hover:text-lak-text"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid={NAV.cta}
          className="hidden xl:inline-flex items-center px-6 py-3 btn-sage text-[11px] tracking-[0.28em] uppercase"
        >
          Start Your Project
        </Link>

        <button
          aria-label="Toggle menu"
          data-testid={NAV.menuToggle}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-lak-text p-2 -mr-2"
        >
          {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
            className="md:hidden border-t border-black/10 bg-lak-bg overflow-hidden"
          >
            <div className="px-6 py-10 flex flex-col">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1], delay: 0.08 + i * 0.06 }}
                  className="overflow-hidden border-b border-black/10 last:border-b-0"
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="block py-5 font-serif text-5xl font-light tracking-tight transition-colors active:text-lak-sage"
                    data-testid={`mobile-${l.id}`}
                  >
                    <span className="flex items-baseline justify-between">
                      <span>{l.label}</span>
                      <span className="eyebrow text-lak-soft text-[10px]">{String(i+1).padStart(2,"0")}</span>
                    </span>
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1], delay: 0.08 + links.length * 0.06 }}
              >
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta"
                  className="mt-8 inline-flex w-full justify-center items-center px-6 py-5 btn-sage text-[11px] tracking-[0.28em] uppercase"
                >
                  Start Your Project
                </Link>
              </motion.div>
              <p className="mt-10 eyebrow text-lak-soft">info@lakatelier.com</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
